import type { AnySchema } from 'yup';

import * as Yup from 'yup';
import { capitalize } from '../lib';

type LazySchema = Yup.Lazy<unknown>;

import REGEXP from './regexp';

function isValidEmailValue(value: string) {
  return REGEXP.email.test(value);
}

function isValidPhoneValue(value: string) {
  const digits = value.replace(/\D/g, '');
  return digits.length >= 8;
}

interface ValidatorMessages {
  required?: string;
  invalid?: string;
  min?: string;
  [key: string]: string | undefined;
}

interface BaseValidatorParams {
  label?: string;
  required?: boolean;
  messages?: ValidatorMessages;
}

class BaseValidator {
  label: string;
  required: boolean;
  messages: ValidatorMessages;

  constructor(params?: BaseValidatorParams) {
    const { label = 'Field', required = true, messages = {} } = params || {};
    this.label = label;
    this.required = required;
    this.messages = {
      ...this.defaultMessages,
      ...messages,
    };
  }

  get defaultMessages(): ValidatorMessages {
    return {
      required: 'Field is required',
    };
  }

  get schema(): AnySchema | LazySchema {
    throw new Error('Not Implemented Error.');
  }
}

class RegexValidator extends BaseValidator {
  get regex(): RegExp {
    throw new Error('Not Implemented Error.');
  }

  get defaultMessages(): ValidatorMessages {
    return {
      invalid: 'Field is invalid',
      required: 'Field is required',
    };
  }

  get schema(): AnySchema | LazySchema {
    const baseSchema = Yup.string().matches(this.regex, this.messages.invalid);
    return this.required
      ? baseSchema.required(this.messages.required)
      : Yup.lazy((value) => (value === '' ? Yup.string() : baseSchema));
  }
}

class EmailValidator extends RegexValidator {
  get regex(): RegExp {
    return REGEXP.email;
  }

  get defaultMessages(): ValidatorMessages {
    return {
      invalid: 'Email is invalid',
      required: 'Field is required',
    };
  }
}

interface PasswordValidatorParams extends BaseValidatorParams {
  min?: number;
}

class PasswordValidator extends BaseValidator {
  min: number;

  constructor(props: PasswordValidatorParams = {}) {
    const { min = 8, ...restProps } = props;
    super(restProps);
    this.min = min;
  }

  get defaultMessages(): ValidatorMessages {
    return {
      min: `${capitalize(this.label)} should have minumum 8 symbols`,
      required: `${capitalize(this.label)} is required`,
    };
  }

  get schema(): AnySchema {
    return this.required
      ? Yup.string()
          .required(this.messages.required)
          .min(this.min, this.messages.min)
      : Yup.string().min(this.min, this.messages.min);
  }
}

const REGISTRY: Record<
  string,
  typeof EmailValidator | typeof PasswordValidator
> = {
  email: EmailValidator,
  password: PasswordValidator,
};

function createValidator(
  key: string,
  params: BaseValidatorParams,
): AnySchema | LazySchema {
  if (key in REGISTRY) {
    const Base = REGISTRY[key];
    return new Base(params).schema;
  }
  throw new Error(`Validator with key=${key} does not exists.`);
}

interface SchemaEntry extends BaseValidatorParams {
  type: string;
}

function createSchema(obj: Record<string, AnySchema | SchemaEntry>) {
  const schema: Record<string, AnySchema | LazySchema> = {};
  for (const [key, value] of Object.entries(obj)) {
    const isYupSchema = Boolean(Yup.isSchema(value));
    schema[key] = isYupSchema
      ? (value as AnySchema)
      : createValidator((value as SchemaEntry).type, value as SchemaEntry);
  }
  return Yup.object(schema);
}

export { createSchema, createValidator, isValidEmailValue, isValidPhoneValue, Yup };
